import { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import pr from '../personnel/PersonRecordPanel.module.css'
import be from '../personnel/BulkEditPanel.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { apiJson, ApiError } from '../../lib/api'
import { TriState, type Tri } from '../personnel/BulkEditPanel'
import { LIFETIMES, LOCATION_ENTRY_OPTIONS, MODES, PRODUCTS, TASKS, TEAM_COLOURS, offered, isRotating, type Team, type TeamOffer } from '../TeamsPage'

// Bulk edit for ticked teams (the Personnel pattern): the settings that make
// sense on many teams at once. A name, a default team and the preassign role
// belong to one team, so they stay in its own panel. Every field starts
// "Unchanged" and only touched fields are sent; the server half is
// POST /api/teams/bulk, which runs each team's own save and changes all of them
// or none.

const CHIP_CAP = 8
const KEEP = '__keep'
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

interface Props {
  teams: Team[]
  sites: { id: string; name: string; archived_at: string | null }[]
  sets: { id: string; name: string }[]
  groups: { id: string; name: string; is_archived: boolean }[]
  offer: TeamOffer | null
  onClose: () => void
  onSaved: (message: string) => void
}

type Pick = { key: string; label: string; options: { value: string; label: string; disabled?: boolean }[] }

export function TeamBulkPanel({ teams, sites, sets, groups, offer, onClose, onSaved }: Props) {
  const { user } = useAuth()
  const n = teams.length
  const [vals, setVals] = useState<Record<string, string>>({})
  const [active, setActive] = useState<Tri>('unchanged')
  const [locRequired, setLocRequired] = useState<Tri>('unchanged')
  const [saveError, setSaveError] = useState<string | null>(null)
  // The server's ask before switching off a team that holds the preassign role.
  const [clearAsk, setClearAsk] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)

  const picks: Pick[] = [
    { key: 'task', label: 'Task', options: TASKS.map((t) => ({ value: t, label: t })) },
    { key: 'orchardId', label: 'Site', options: sites.filter((s) => !s.archived_at).map((s) => ({ value: s.id, label: s.name })) },
    { key: 'productId', label: 'Primary product', options: [{ value: '', label: 'None' }, ...PRODUCTS.map((p) => ({ value: p, label: p }))] },
    { key: 'mode', label: 'App mode', options: MODES.map((m) => ({ value: m.key, label: m.key, disabled: !offered(offer?.modes, m.key) })) },
    {
      key: 'payBasis',
      label: 'Pay basis',
      options: [
        { value: 'hourly', label: 'Hourly', disabled: !offered(offer?.pay_bases, 'hourly') },
        { value: 'piece_rate', label: 'Piece rate', disabled: !offered(offer?.pay_bases, 'piece_rate') },
      ],
    },
    { key: 'groupId', label: 'Group', options: [{ value: '', label: 'Default group' }, ...groups.filter((g) => !g.is_archived).map((g) => ({ value: g.id, label: g.name }))] },
    { key: 'qualitySetId', label: 'Quality control', options: [{ value: '', label: 'No quality control' }, ...sets.map((s) => ({ value: s.id, label: s.name }))] },
    { key: 'codeLife', label: 'Code lifetime', options: LIFETIMES.filter((l) => !l.legacy).map((l) => ({ value: l.key, label: l.option ?? l.label })) },
    { key: 'locationEntry', label: 'How pickers set their location', options: LOCATION_ENTRY_OPTIONS.map((x) => ({ value: x.value, label: x.label })) },
    { key: 'colour', label: 'Colour', options: TEAM_COLOURS.map((c) => ({ value: c.hex, label: c.name })) },
  ]
  const touched = picks.filter((p) => vals[p.key] !== undefined && vals[p.key] !== KEEP)
  const labelOf = (p: Pick) => p.options.find((x) => x.value === vals[p.key])?.label || vals[p.key]

  const changes: string[] = touched.map((p) => `${p.label} ${labelOf(p)}`)
  if (active !== 'unchanged') changes.push(active === 'on' ? 'active' : 'inactive')
  if (locRequired !== 'unchanged') changes.push(`location required ${locRequired}`)

  // Said here before the server says it: an induction team never rotates its
  // code. The server checks every rule again, team by team.
  const missing: string[] = []
  const task = vals.task && vals.task !== KEEP ? vals.task : null
  const life = vals.codeLife && vals.codeLife !== KEEP ? vals.codeLife : null
  if (life && isRotating(life)) {
    const inductions = teams.filter((t) => (task || t.task) === 'Induction')
    if (inductions.length) missing.push(`pick a lifetime of 1 day or more, or untick ${inductions.map((t) => t.name).join(', ')}: an induction team cannot rotate its code`)
  }
  const blocked = missing.length > 0

  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, () => !busy && onClose(), { dirty: changes.length > 0 && !busy, el: panelEl, modal: true, focusFirst: true, busy })

  const save = async (confirmClear = false) => {
    if (blocked || busy || !changes.length) return
    setBusy(true)
    setSaveError(null)
    const body: Record<string, unknown> = { ids: teams.map((t) => t.id) }
    for (const p of touched) {
      const v = vals[p.key]
      body[p.key] = ['productId', 'groupId', 'qualitySetId', 'locationEntry'].includes(p.key) ? v || null : v
    }
    if (active !== 'unchanged') body.active = active === 'on'
    if (locRequired !== 'unchanged') body.locationRequired = locRequired === 'on'
    if (confirmClear) body.confirmClearPreassign = true
    try {
      const r = await apiJson<{ updated: number }>(user, '/api/teams/bulk', { body })
      onSaved(`${plural(r.updated, 'team', 'teams')} updated: ${changes.join(', ')}`)
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'PREASSIGN_CLEAR_CONFIRM') setClearAsk(e.body.error)
      else setSaveError(e?.message || 'Nothing was changed.')
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${be.drawer}`} role="dialog" aria-label={`Edit ${n} teams`}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>Edit {n} teams</h2>
            <p className={o.drawerSub}>Anything left on Unchanged stays as it is on each team. Names, default teams and the preassign role are set on each team.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={be.chips} aria-label="Selected teams" data-testid="bulk-chips">
          {teams.slice(0, CHIP_CAP).map((t) => (
            <span key={t.id} className={be.chip}>
              {t.name}
            </span>
          ))}
          {n > CHIP_CAP && <span className={be.more}>and {n - CHIP_CAP} more</span>}
        </div>

        <fieldset className={`${o.drawerBody} ${be.body} ${inertClass(busy, slow)}`} disabled={busy} aria-busy={busy}>
          <div className={o.grid2}>
            {picks.map((p) => (
              <div key={p.key} className={o.field}>
                <label className={o.fieldLabel} htmlFor={`tb-${p.key}`}>
                  {p.label}
                </label>
                <select
                  id={`tb-${p.key}`}
                  className={o.input}
                  value={vals[p.key] ?? KEEP}
                  onChange={(e) => {
                    setVals({ ...vals, [p.key]: e.target.value })
                    setClearAsk(null)
                  }}
                >
                  <option value={KEEP}>Unchanged</option>
                  {p.options.map((x) => (
                    <option key={x.value || 'none'} value={x.value} disabled={x.disabled}>
                      {x.label}
                      {x.disabled ? ' (not turned on)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {vals.codeLife && vals.codeLife !== KEEP && (
            <span className={o.fieldHint}>
              {isRotating(vals.codeLife)
                ? 'Under a day the code rotates on a supervisor’s phone, and printed cards stop working.'
                : 'Teams that rotate today get a fixed code, so their cards need printing.'}
            </span>
          )}

          <div className={`${o.toggleRow} ${be.triRow}`}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Active</div>
              <div className={o.toggleSub}>
                {active === 'off'
                  ? `Nobody can clock on to the ${n} until they are switched back on. A team holding a site’s preassign role gives it up, and you are asked first.`
                  : active === 'on'
                    ? `All ${n} can be clocked on to.`
                    : `Active now: ${teams.filter((t) => t.active).length} of ${n}.`}
              </div>
            </div>
            <TriState
              label="Active"
              value={active}
              options={['Active', 'Inactive']}
              onChange={(v) => {
                setActive(v)
                setClearAsk(null)
              }}
            />
          </div>

          <div className={`${o.toggleRow} ${be.triRow}`}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Location required</div>
              <div className={o.toggleSub}>{`Required now on ${teams.filter((t) => t.location_required).length} of ${n}.`}</div>
            </div>
            <TriState label="Location required" value={locRequired} options={['On', 'Off']} onChange={setLocRequired} />
          </div>
          <span className={o.fieldHint}>Changing a team’s settings means reprinting its clock-on QR.</span>

          {clearAsk && (
            <div className={`${o.warn} ${pr.block}`} role="alert" data-testid="clear-preassign">
              <p>{clearAsk}</p>
              <button type="button" className={o.ghost} onClick={() => save(true)}>
                Switch off and clear the role
              </button>
            </div>
          )}
        </fieldset>

        <div className={`${o.drawerFoot} ${pr.foot}`}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <span className={blocked ? o.footNote : `${o.footNote} ${be.changeNote}`} data-testid="bulk-changes">
            {blocked ? `First ${missing.join('; ')}.` : changes.length ? `Changes: ${changes.join(', ')}` : 'Nothing has changed yet'}
          </span>
          <PendingButton className={o.primary} busy={busy} pending="Applying…" disabled={blocked || !changes.length} onClick={() => save()}>
            {`Apply to ${n}`}
          </PendingButton>
          <Failed text={saveError && `${saveError} No team was changed.`} />
        </div>
      </div>
      {confirm}
    </div>
  )
}
